import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLanguage } from '../context/LanguageContext';
import { staffAPI } from '../services/api';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';

const schema = yup.object().shape({
    name: yup.string().required('Name is required'),
    mobile: yup.string().required('Mobile is required'),
    username: yup.string().required('Username is required').min(4, 'Username must be at least 4 characters'),
    password: yup.string().min(6, 'Password must be at least 6 characters').when('$isEdit', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Password is required'),
    }),
});

const StaffManagement = () => {
    const { t } = useLanguage();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        context: { isEdit: !!editId },
    });

    useEffect(() => {
        fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchStaff = async () => {
        setLoading(true);
        try {
            const response = await staffAPI.list();
            setStaff(response.data.data || response.data);
        } catch (error) {
            toast.error(t('staff.failedLoad'));
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (data) => {
        try {
            if (editId) {
                if (!data.password) {
                    delete data.password;
                }
                await staffAPI.update(editId, data);
                toast.success(t('staff.updatedSuccess'));
            } else {
                await staffAPI.create(data);
                toast.success(t('staff.createdSuccess'));
            }
            setShowModal(false);
            reset();
            setEditId(null);
            fetchStaff();
        } catch (error) {
            const message = error.response?.data?.message || t('staff.failedCreate');
            toast.error(message);
        }
    };

    const handleEdit = (member) => {
        setEditId(member.id);
        reset({
            name: member.name,
            mobile: member.mobile,
            username: member.username,
            password: '',
        });
        setShowModal(true);
    };

    const handleDelete = (id) => {
        setDeleteId(id);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        setShowDeleteConfirm(false);
        try {
            await staffAPI.delete(deleteId);
            toast.success(t('staff.deletedSuccess'));
            fetchStaff();
        } catch (error) {
            toast.error(t('staff.failedDelete'));
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            await staffAPI.toggleStatus(id);
            toast.success(t('staff.statusUpdated'));
            fetchStaff();
        } catch (error) {
            toast.error(t('staff.failedStatus'));
        }
    };

    const openAddModal = () => {
        setEditId(null);
        reset({ name: '', mobile: '', username: '', password: '' });
        setShowModal(true);
    };

    return (
        <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">
                        {t('staff.title')}
                    </h1>
                    <button
                        onClick={openAddModal}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        {t('staff.addStaff')}
                    </button>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : staff.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        {t('common.noData')}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('staff.name')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('staff.mobile')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('staff.username')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('staff.status')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('common.actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {staff.map((member) => (
                                    <tr key={member.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {member.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {member.mobile}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {member.username}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className={`px-2 py-1 text-xs rounded-full ${
                                                    member.status === 'active'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                }`}
                                            >
                                                {member.status === 'active'
                                                    ? t('staff.active')
                                                    : t('staff.inactive')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                                            <button
                                                onClick={() => handleEdit(member)}
                                                className="text-blue-600 hover:text-blue-800"
                                            >
                                                {t('common.edit')}
                                            </button>
                                            <button
                                                onClick={() => handleToggleStatus(member.id)}
                                                className="text-yellow-600 hover:text-yellow-800"
                                            >
                                                {t('staff.toggleStatus')}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(member.id)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                {t('common.delete')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-semibold mb-4">
                            {editId ? t('staff.editStaff') : t('staff.addStaff')}
                        </h2>
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('staff.name')} *
                                </label>
                                <input
                                    type="text"
                                    {...register('name')}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        errors.name ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                {errors.name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('staff.mobile')} *
                                </label>
                                <input
                                    type="text"
                                    {...register('mobile')}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        errors.mobile ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                {errors.mobile && (
                                    <p className="text-red-500 text-sm mt-1">{errors.mobile.message}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('staff.username')} *
                                </label>
                                <input
                                    type="text"
                                    {...register('username')}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        errors.username ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                {errors.username && (
                                    <p className="text-red-500 text-sm mt-1">{errors.username.message}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('staff.password')} {!editId && '*'}
                                </label>
                                <input
                                    type="password"
                                    {...register('password')}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                        errors.password ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder={editId ? 'Leave blank to keep current' : ''}
                                />
                                {errors.password && (
                                    <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
                                )}
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowModal(false);
                                        reset();
                                        setEditId(null);
                                    }}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    {t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title={t('common.areYouSure')}
                message={t('staff.deleteConfirm')}
                onConfirm={confirmDelete}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </div>
    );
};

export default StaffManagement;